import React, {useEffect, useState} from 'react';
import classNames from 'classnames';
import TranslatedOption from '../../common/TranslatedOption';
import type {
	RuleConditionState,
	CounterState,
	DurationState,
	DeviceElementTarget
} from '../../../modules/ruleengine/types';
import TriggerOptionalSettingsInput from './TriggerOptionalSettingsInput';
import type {SettingOption} from './constants';
import {NONE, AFTER_X_HOURS, AFTER_X_MINUTES, AFTER_X_TIMES, DATAPOINTS_WITHOUT_DURATION_SETTINGS} from './constants';
import hasAuthority from '../../common/hasAuthority';
import {AUTHORITY_READ_RULE_OPTIONAL_SETTING} from '../../../modules/common/consts';
type TriggerOptionalSettingsProps = {
	canEdit: boolean;
	onChange: (arg0: Partial<RuleConditionState>) => void;
	counter?: CounterState;
	duration?: DurationState;
	elementTarget: DeviceElementTarget;
};
type Props = TriggerOptionalSettingsProps;

const getSettingFromState = (counterState: CounterState | undefined, durationState: DurationState | undefined) => {
	if (counterState && counterState.conditionCount) {
		return AFTER_X_TIMES;
	}

	if (durationState && durationState.durationInMin) {
		if (durationState.durationInMin % 60 === 0) {
			return AFTER_X_HOURS;
		} else {
			return AFTER_X_MINUTES;
		}
	}

	return NONE;
};

const TriggerOptionalSettings = ({
	canEdit,
	onChange,
	counter: counterState,
	duration: durationState,
	elementTarget
}: Props) => {
	const canSeeRuleOptionalSettings = hasAuthority(AUTHORITY_READ_RULE_OPTIONAL_SETTING);
	const [selectedSetting, setSelectedSetting] = useState<SettingOption>(
		getSettingFromState(counterState, durationState)
	);
	const [canHaveDuration, setCanHaveDuration] = useState<boolean>(true);
	useEffect(() => {
		if (DATAPOINTS_WITHOUT_DURATION_SETTINGS.includes(elementTarget.dataPoint)) {
			setCanHaveDuration(false);

			if (selectedSetting === AFTER_X_HOURS || selectedSetting === AFTER_X_MINUTES) {
				setSelectedSetting(NONE);
			}
		} else {
			setCanHaveDuration(true);
		}
	}, [elementTarget.dataPoint, selectedSetting]);
	return (
		<div
			className={classNames('trigger-optional-settings', {
				disabled: !canSeeRuleOptionalSettings
			})}>
			<select
				data-testid="optional-setting"
				disabled={!canEdit}
				onChange={({target: {value}}) => {
					setSelectedSetting(value as SettingOption);
				}}
				value={selectedSetting}>
				<TranslatedOption
					key="none"
					trans={{
						id: 'ruleengine.edit.rule.optionalSettingNone',
						defaultValue: '--'
					}}
					value="NONE"
				/>
				<TranslatedOption
					key="times"
					trans={{
						id: 'ruleengine.edit.rule.optionalSettingAfterXTimes',
						defaultValue: 'After x times'
					}}
					value={AFTER_X_TIMES}
				/>
				{canHaveDuration && (
					<TranslatedOption
						key="minutes"
						trans={{
							id: 'ruleengine.edit.rule.optionalSettingAfterXMinutes',
							defaultValue: 'After x minutes'
						}}
						value={AFTER_X_MINUTES}
					/>
				)}
				{canHaveDuration && (
					<TranslatedOption
						key="hours"
						trans={{
							id: 'ruleengine.edit.rule.optionalSettingAfterXHours',
							defaultValue: 'After x hours'
						}}
						value={AFTER_X_HOURS}
					/>
				)}
			</select>
			<TriggerOptionalSettingsInput
				disabled={!canEdit}
				selectedSetting={selectedSetting}
				counterState={counterState}
				durationState={durationState}
				onChange={onChange}
			/>
		</div>
	);
};

export default TriggerOptionalSettings;
